import { drawClock } from './clocks';
import type { Line } from '../data/letter';
import { esc } from './forms';
import { TITLE } from '../data/letter';
import { CREDIT } from '../data/music';
import { formatDuration } from '../physics/schwarzschild';
import type { ScreenLabel } from '../chapters/types';

/** The line under your clock: how far behind Earth it is, and how much slower it is running right now. */
export function clockOffset(gapSeconds: number, rate: number): { behind: string; pace: string } {
  const slower = 1 - rate;
  let pace: string;
  if (!(slower > 1e-7)) pace = 'same pace as Earth';
  else if (slower < 1e-4) pace = `${Number((slower * 1e6).toPrecision(2))} millionths slower here`;
  else if (slower < 0.01) pace = `${(slower * 100).toFixed(2)}% slower here`;
  else pace = `${(slower * 100).toFixed(1)}% slower here`;
  const behind = gapSeconds >= 1 ? `${formatDuration(gapSeconds)} behind Earth` : 'in step with Earth';
  return { behind, pace };
}

export interface HudCallbacks {
  onSound: () => void;
  onQuality: () => void;
  onPause: () => void;
  onPrev: () => void;
  onPlayPause: () => void;
  onNext: () => void;
  onVolume: (v: number) => void;
  /** the chapter list in the menu, and the segments across the top */
  onChapter: (index: number) => void;
  onChapterBack: () => void;
  onChapterNext: () => void;
  /** "Show me": the chapter's demonstration */
  onDemo: () => void;
  /** a tap on the right side of the picture (or Enter, Space, Right): go on */
  onTapRight: () => void;
  /** a tap on the left side (or Left): back a line */
  onTapLeft: () => void;
  /** skip this chapter's exercise */
  onSkip: () => void;
}

export type LeverConfig =
  | { kind: 'slider'; label: string; min: number; max: number; step: number; value: number; format?: (v: number) => string; onInput: (v: number) => void; action?: { label: string; onClick: () => void } }
  | { kind: 'buttons'; note?: string; items: { label: string; primary?: boolean; pressed?: boolean; onClick: () => void }[] };

const QUALITY_NOTE = {
  cinematic: 'Cinematic draws the full picture. Fast draws at a lower resolution for smoother motion on a phone or an older machine; the story is the same.',
  fast: 'Fast draws at a lower resolution for smoother motion. Cinematic draws the full picture.'
};

/**
 * The frame around every chapter, told like a story: the picture fills the screen, five segments across the top
 * count the chapters, one caption at a time sits over the picture with no box, a tap on the right goes on and a
 * tap on the left goes back, and the one control of the moment floats as a pill. Everything secondary lives
 * behind one menu button. Chapters never touch the DOM directly.
 */
export class Hud {
  private root: HTMLElement;
  private labelsRoot: HTMLElement;
  private cb: HudCallbacks;
  private segs!: HTMLElement;
  private chapterTitle!: HTMLElement; private chapterNo!: HTMLElement;
  private menuBtn!: HTMLButtonElement;
  private clocks!: HTMLElement;
  private ck1!: HTMLCanvasElement; private ck2!: HTMLCanvasElement;
  private ckE!: HTMLElement; private ckY!: HTMLElement; private ckO!: HTMLElement; private ckP!: HTMLElement;
  private zones!: HTMLElement; private cue!: HTMLElement;
  private hint!: HTMLElement;
  private cap!: HTMLElement;
  private leverBox!: HTMLElement;
  private demoBtn!: HTMLButtonElement; private skipBtn!: HTMLButtonElement; private nextBtn!: HTMLButtonElement;
  private firstHint!: HTMLElement;
  private inter!: HTMLElement; private interNo!: HTMLElement; private interTitle!: HTMLElement; private interBridge!: HTMLElement; private interPlace!: HTMLElement;
  private sheet!: HTMLElement;
  private bSound!: HTMLButtonElement; private bQuality!: HTMLButtonElement; private bPause!: HTMLButtonElement;
  private qualityNote!: HTMLElement;
  private chapterList!: HTMLElement;
  private readouts!: HTMLElement; private notes!: HTMLElement;
  private player!: HTMLElement; private pTitle!: HTMLElement; private pPlay!: HTMLButtonElement;
  private labelEls: HTMLElement[] = [];

  private active = 0;
  private voices = { lover: 'Me', expert: 'The guide' };
  private lastSay = '';
  private lastClockText = '';
  private lastHint = '';
  /** the beats: lines still to show, the ones shown (for going back), and the state of the action */
  private queue: Line[] = [];
  private history: Line[] = [];
  private busy = false;
  private fadeTimer = 0;
  private pendingLever: LeverConfig | null = null;
  private pendingMove: string | null = null;
  private moveShown = false;
  private pendingDemo: string | null = null;
  private skipOn = false;
  private pendingOnward: string | null = null;
  private tapAlways = false;
  private hinted = false;

  constructor(root: HTMLElement, labelsRoot: HTMLElement, cb: HudCallbacks) {
    this.cb = cb;
    this.root = root;
    this.labelsRoot = labelsRoot;
    root.innerHTML = `
      <div class="inter" id="inter" hidden>
        <div class="interIn"><div class="interNo" id="interNo"></div><div class="interTitle" id="interTitle"></div><p class="interBridge" id="interBridge"></p><p class="interPlace" id="interPlace"></p></div>
      </div>
      <div class="segs" id="segs" role="list" aria-label="Chapters"></div>
      <div class="topbar">
        <h2 class="chap"><span class="chapterTitle" id="chapterTitle"></span><span class="chapterNo" id="chapterNo"></span></h2>
        <button type="button" class="menuBtn" id="menuBtn" aria-haspopup="dialog" aria-expanded="false" aria-controls="sheet" aria-label="Sound, pause, picture, music and details">&#8943;</button>
      </div>
      <div class="clocks" id="clocks" hidden>
        <div class="clock earth"><canvas id="ck1" width="152" height="152" aria-hidden="true"></canvas><span class="ctxt"><span class="cl">Earth</span><span class="ct" id="ckE"></span></span></div>
        <div class="clock you"><canvas id="ck2" width="152" height="152" aria-hidden="true"></canvas><span class="ctxt"><span class="cl">You</span><span class="ct" id="ckY"></span><span class="co" id="ckO"></span><span class="cp" id="ckP"></span></span></div>
      </div>
      <div class="zones" id="zones" hidden>
        <button type="button" class="zone zoneL" id="zoneL" aria-label="Back a line"></button>
        <button type="button" class="zone zoneR" id="zoneR" aria-label="Go on"></button>
      </div>
      <div class="cue" id="cue" hidden aria-hidden="true">&#8250;</div>
      <div class="scrim" aria-hidden="true"></div>
      <div class="foot">
        <div class="hint" id="hint" aria-live="polite"></div>
        <div class="cap" id="say" aria-live="polite"></div>
        <div class="pills" id="pills">
          <div class="lever" id="lever" hidden></div>
          <button type="button" class="pill ghost" id="showMe" hidden>Show me</button>
          <button type="button" class="pill ghost" id="skip" hidden>Next chapter &#8250;</button>
          <button type="button" class="pill gold" id="next" hidden>Next &#8250;</button>
        </div>
        <div class="firstHint" id="firstHint" hidden>Tap the right side of the picture to go on. The left side goes back.</div>
      </div>
      <div class="sheet" id="sheet" hidden role="dialog" aria-label="Menu">
        <div class="sheetHead"><div class="brand">${esc(TITLE.name)}<small>${esc(TITLE.tagline)}</small></div><button type="button" id="sheetClose" class="menuBtn" aria-label="Close the menu">&times;</button></div>
        <div class="rowBtns">
          <button type="button" id="hudSound" aria-pressed="false">Sound off</button>
          <button type="button" id="hudPause" aria-pressed="false">Pause</button>
          <button type="button" id="hudQuality" aria-pressed="true">Cinematic</button>
        </div>
        <p class="sub" id="qualityNote"></p>
        <h4>Chapters</h4>
        <div class="chapterList" id="chapterList"></div>
        <div class="rowBtns"><button type="button" id="navBack">&#8249; Previous chapter</button><button type="button" id="navNext">Next chapter &#8250;</button></div>
        <h4>Music</h4>
        <div class="player" id="player" hidden>
          <button type="button" id="pPrev" aria-label="Previous track">&#9198;</button>
          <button type="button" id="pPlay" aria-label="Play or pause">&#9654;</button>
          <button type="button" id="pNext" aria-label="Next track">&#9197;</button>
          <span class="ptitle" id="pTitle"></span>
          <input type="range" id="pVol" min="0" max="100" value="75" aria-label="Music volume">
        </div>
        <h4>The numbers</h4>
        <dl class="readouts" id="readouts"></dl>
        <h4>The science</h4>
        <div class="notes" id="notes"></div>
        <p class="legal">${esc(CREDIT)}</p>
      </div>`;
    const q = <T extends HTMLElement>(id: string) => root.querySelector<T>('#' + id)!;
    this.segs = q('segs'); this.chapterTitle = q('chapterTitle'); this.chapterNo = q('chapterNo');
    this.menuBtn = q<HTMLButtonElement>('menuBtn'); this.menuBtn.addEventListener('click', () => this.toggleDrawer());
    this.clocks = q('clocks');
    this.ck1 = q<HTMLCanvasElement>('ck1'); this.ck2 = q<HTMLCanvasElement>('ck2');
    this.ckE = q('ckE'); this.ckY = q('ckY'); this.ckO = q('ckO'); this.ckP = q('ckP');
    this.zones = q('zones'); this.cue = q('cue');
    q('zoneL').addEventListener('click', cb.onTapLeft); q('zoneR').addEventListener('click', () => { this.hinted = true; this.firstHint.hidden = true; cb.onTapRight(); });
    this.hint = q('hint'); this.cap = q('say');
    this.leverBox = q('lever');
    this.demoBtn = q<HTMLButtonElement>('showMe'); this.demoBtn.addEventListener('click', cb.onDemo);
    this.skipBtn = q<HTMLButtonElement>('skip'); this.skipBtn.addEventListener('click', cb.onSkip);
    this.nextBtn = q<HTMLButtonElement>('next'); this.nextBtn.addEventListener('click', cb.onChapterNext);
    this.firstHint = q('firstHint');
    this.inter = q('inter'); this.interNo = q('interNo'); this.interTitle = q('interTitle'); this.interBridge = q('interBridge'); this.interPlace = q('interPlace');
    this.inter.addEventListener('click', cb.onTapRight);
    this.sheet = q('sheet');
    q('sheetClose').addEventListener('click', () => this.toggleDrawer(false));
    this.bSound = q<HTMLButtonElement>('hudSound'); this.bQuality = q<HTMLButtonElement>('hudQuality'); this.bPause = q<HTMLButtonElement>('hudPause');
    this.qualityNote = q('qualityNote');
    this.bSound.addEventListener('click', cb.onSound); this.bQuality.addEventListener('click', cb.onQuality); this.bPause.addEventListener('click', cb.onPause);
    this.chapterList = q('chapterList');
    q('navBack').addEventListener('click', () => { this.toggleDrawer(false); cb.onChapterBack(); });
    q('navNext').addEventListener('click', () => { this.toggleDrawer(false); cb.onChapterNext(); });
    this.readouts = q('readouts'); this.notes = q('notes');
    this.player = q('player'); this.pTitle = q('pTitle'); this.pPlay = q<HTMLButtonElement>('pPlay');
    q('pPrev').addEventListener('click', cb.onPrev); this.pPlay.addEventListener('click', cb.onPlayPause); q('pNext').addEventListener('click', cb.onNext);
    q<HTMLInputElement>('pVol').addEventListener('input', (e) => cb.onVolume((e.target as HTMLInputElement).valueAsNumber / 100));
  }

  show() { this.root.hidden = false; }
  hide() { this.root.hidden = true; this.setLabels([]); this.toggleDrawer(false); }

  // ---- the chapters across the top

  /** The segments: one per chapter, each a button. The active one fills as the chapter's beats go by. */
  setProgress(items: { id: string; title: string }[], active: number) {
    this.active = active;
    this.segs.innerHTML = items.map((it, i) => `<button type="button" class="seg${i < active ? ' done' : i === active ? ' active' : ''}" data-i="${i}" role="listitem" title="${esc(it.title)}" aria-label="${esc(it.title)}"${i === active ? ' aria-current="step"' : ''}><span class="bar"><span class="fill" style="width:${i < active ? 100 : 0}%"></span></span></button>`).join('');
    this.segs.querySelectorAll<HTMLButtonElement>('button').forEach((b) => b.addEventListener('click', () => this.cb.onChapter(Number(b.dataset.i))));
    this.chapterList.innerHTML = items.map((it, i) => `<button type="button" data-i="${i}"${i === active ? ' aria-pressed="true"' : ''}>${esc(it.title)}</button>`).join('');
    this.chapterList.querySelectorAll<HTMLButtonElement>('button').forEach((b) => b.addEventListener('click', () => { this.toggleDrawer(false); this.cb.onChapter(Number(b.dataset.i)); }));
    this.updateBeat();
  }
  setChapter(title: string, no = 0, count = 0) {
    this.chapterTitle.textContent = title;
    this.chapterNo.textContent = no > 0 && count > 0 ? `${no} of ${count}` : '';
  }
  /** How far through this chapter's beats we are, drawn as the active segment's fill. */
  private updateBeat() {
    const seg = this.segs.querySelector<HTMLElement>(`.seg[data-i="${this.active}"] .fill`);
    if (!seg) return;
    if (this.pendingOnward && this.queue.length === 0) { seg.style.width = '100%'; return; }
    const shown = this.history.length, ahead = this.queue.length + (this.pendingMove && !this.moveShown ? 1 : 0) + (this.pendingOnward ? 0 : 1);
    seg.style.width = `${Math.round((shown / Math.max(1, shown + ahead)) * 100)}%`;
  }

  /** Who speaks: the lover's name and the guide's label, shown above every caption. */
  setVoices(v: { lover: string; expert: string }) { this.voices = v; }

  // ---- the beats: one caption at a time

  /** Queue lines in their voices. The first shows now; a tap on the right steps through the rest. */
  say(lines: Line[]) {
    const key = lines.map((l) => l.voice + l.text).join('|'); if (key === this.lastSay) return; this.lastSay = key;
    this.queue.push(...lines);
    if (!this.busy) this.advance();
  }
  clearSay() {
    this.lastSay = ''; this.queue = []; this.history = []; this.busy = false; this.moveShown = false;
    window.clearTimeout(this.fadeTimer); this.cap.innerHTML = ''; this.cap.classList.remove('move'); this.cap.style.opacity = '1';
    this.syncAction();
  }
  /** True while a tap on the picture steps the words (lines queued, the Next pill up, or the epilogue). */
  get isReading() { return this.busy || (this.pendingOnward !== null && this.queue.length === 0) || this.tapAlways; }
  /** The epilogue keeps the tap zones on even with nothing queued. */
  setTapAlways(on: boolean) { this.tapAlways = on; this.syncAction(); }
  /** Step to the next beat. Returns false when there is nothing to step to (the action, or the end). */
  goOn(): boolean {
    if (this.queue.length > 0) { this.advance(); return true; }
    if (this.pendingMove && !this.moveShown) { this.advance(); return true; }
    return false;
  }
  /** Back one line. Returns false at the first line. */
  goBack(): boolean {
    if (this.history.length < 2) return false;
    const current = this.history.pop()!;
    const prev = this.history.pop()!;
    this.queue.unshift(current);
    this.moveShown = false;
    this.queue.unshift(prev);
    this.advance();
    return true;
  }
  private advance() {
    const l = this.queue.shift();
    if (!l) {
      // the lines are read: the instruction is its own beat, with the control under it
      if (this.pendingMove && !this.moveShown) { this.moveShown = true; this.busy = false; this.renderCaption(null); this.syncAction(); }
      return;
    }
    this.history.push(l);
    this.busy = this.queue.length > 0 || (!!this.pendingMove && !this.moveShown);
    this.renderCaption(l);
    this.syncAction();
  }
  private renderCaption(l: Line | null) {
    window.clearTimeout(this.fadeTimer);
    this.cap.style.opacity = '0';
    this.fadeTimer = window.setTimeout(() => {
      if (l) { this.cap.classList.remove('move'); this.cap.innerHTML = `<p class="${l.voice}"><b class="who">${esc(this.voices[l.voice])}</b>${esc(l.text)}</p>`; }
      else if (this.pendingMove) { this.cap.classList.add('move'); this.cap.innerHTML = `<p><b class="who">Your move</b>${esc(this.pendingMove)}</p>`; }
      else { this.cap.innerHTML = ''; this.cap.classList.remove('move'); }
      this.cap.style.opacity = '1';
    }, 160);
  }

  // ---- the action: one control, held back until the words are read

  private actionOn() { return this.queue.length === 0 && (!this.pendingMove || this.moveShown); }
  private syncAction() {
    const on = this.actionOn();
    this.renderLever(on ? this.pendingLever : null);
    this.demoBtn.hidden = !(on && this.pendingDemo); if (this.pendingDemo) this.demoBtn.textContent = this.pendingDemo;
    this.skipBtn.hidden = !(on && this.skipOn && !this.pendingOnward);
    this.skipBtn.innerHTML = this.active < this.segs.children.length - 1 ? 'Next chapter &#8250;' : 'The measure &#8250;';
    this.nextBtn.hidden = !(on && this.pendingOnward); if (this.pendingOnward) this.nextBtn.innerHTML = esc(this.pendingOnward) + ' &#8250;';
    this.zones.hidden = !this.isReading;
    this.cue.hidden = !(this.busy || (this.pendingOnward !== null && this.queue.length === 0));
    this.firstHint.hidden = this.hinted || !this.busy;
    this.updateBeat();
  }
  setLever(cfg: LeverConfig | null) { this.pendingLever = cfg; this.syncAction(); }
  setMove(text: string | null) {
    const had = this.pendingMove;
    this.pendingMove = text;
    if (!text) { if (had && this.moveShown && this.queue.length === 0) this.renderCaption(null); this.moveShown = false; }
    else if (this.moveShown && this.queue.length === 0) this.renderCaption(null);   // a new instruction mid-action
    else if (!this.busy && this.queue.length === 0 && this.history.length === 0) { this.moveShown = true; this.renderCaption(null); }   // no lines at all: straight to the instruction
    this.syncAction();
  }
  setDemo(label: string | null) { this.pendingDemo = label; this.syncAction(); }
  setSkip(on: boolean) { this.skipOn = on; this.syncAction(); }
  /** The chapter is finished: once the last discovery line is read, the Next pill appears and a right tap goes on. */
  setOnward(label: string | null) { this.pendingOnward = label; this.syncAction(); }
  /** Kept for the director; the segments carry the position now. */
  setNav(index: number, count: number) { this.active = index; this.chapterNo.textContent = index < count ? `${index + 1} of ${count}` : ''; }

  private renderLever(cfg: LeverConfig | null) {
    this.leverBox.innerHTML = '';
    if (!cfg) { this.leverBox.hidden = true; return; }
    this.leverBox.hidden = false;
    if (cfg.kind === 'slider') {
      const id = 'lever-' + Math.random().toString(36).slice(2, 7);
      const fmt = cfg.format ?? ((v: number) => String(v));
      const pill = document.createElement('div'); pill.className = 'sliderPill';
      pill.innerHTML = `<label for="${id}"><span>${esc(cfg.label)}</span><input type="range" id="${id}" min="${cfg.min}" max="${cfg.max}" step="${cfg.step}" value="${cfg.value}"></label><span class="read">${esc(fmt(cfg.value))}</span>`;
      const input = pill.querySelector<HTMLInputElement>('input')!, read = pill.querySelector<HTMLElement>('.read')!;
      input.addEventListener('input', () => { read.textContent = fmt(input.valueAsNumber); cfg.onInput(input.valueAsNumber); });
      this.leverBox.appendChild(pill);
      if (cfg.action) { const b = document.createElement('button'); b.type = 'button'; b.className = 'pill gold'; b.textContent = cfg.action.label; b.addEventListener('click', cfg.action.onClick); this.leverBox.appendChild(b); }
    } else {
      if (cfg.note) { const n = document.createElement('span'); n.className = 'pill note'; n.textContent = cfg.note; this.leverBox.appendChild(n); }
      for (const it of cfg.items) {
        const b = document.createElement('button'); b.type = 'button'; b.className = 'pill ' + (it.primary ? 'gold' : 'ghost'); b.textContent = it.label;
        if (it.pressed !== undefined) b.setAttribute('aria-pressed', String(it.pressed));
        b.addEventListener('click', it.onClick); this.leverBox.appendChild(b);
      }
    }
  }
  /** Move a slider lever to a value from code (a demonstration, or a snap to the exact answer). */
  setLeverValue(v: number) {
    const cfg = this.pendingLever;
    if (!cfg || cfg.kind !== 'slider') return;
    cfg.value = v;
    const input = this.leverBox.querySelector<HTMLInputElement>('input[type=range]'), read = this.leverBox.querySelector<HTMLElement>('.read');
    if (!input) return;
    input.value = String(v);
    if (read) read.textContent = (cfg.format ?? ((x: number) => String(x)))(v);
  }
  /** Update a pressed state on a buttons lever without rebuilding it. */
  setLeverPressed(label: string, pressed: boolean) {
    for (const b of this.leverBox.querySelectorAll('button')) if (b.textContent === label) b.setAttribute('aria-pressed', String(pressed));
  }

  // ---- the interstitial between chapters

  showInter(no: string, title: string, bridge: string, place: string) {
    this.interNo.textContent = no; this.interTitle.textContent = title; this.interBridge.textContent = bridge; this.interPlace.textContent = place;
    this.inter.hidden = false;
    this.inter.classList.remove('out');
    requestAnimationFrame(() => this.inter.classList.add('in'));
  }
  hideInter() {
    if (this.inter.hidden) return;
    this.inter.classList.remove('in'); this.inter.classList.add('out');
    window.setTimeout(() => { this.inter.hidden = true; this.inter.classList.remove('out'); }, 520);
  }
  get interUp() { return !this.inter.hidden && !this.inter.classList.contains('out'); }

  // ---- the rest of the frame

  setReadouts(pairs: [string, string][]) {
    this.readouts.innerHTML = pairs.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`).join('');
    this.readouts.hidden = pairs.length === 0;
  }
  setNotes(paragraphs: string[]) { this.notes.innerHTML = paragraphs.map((p) => `<p>${esc(p)}</p>`).join(''); }
  toggleDrawer(open?: boolean) {
    const o = open ?? this.sheet.hidden;
    this.sheet.hidden = !o;
    this.menuBtn.setAttribute('aria-expanded', String(o));
    this.root.classList.toggle('sheetOpen', o);
  }
  setClocksVisible(v: boolean) { this.clocks.hidden = !v; }
  setClocks(earthSeconds: number, shipSeconds: number, rate = 1) {
    if (this.clocks.hidden) return;
    drawClock(this.ck1, earthSeconds, '#8cc1de'); drawClock(this.ck2, shipSeconds, '#e3b45a');
    const o = clockOffset(earthSeconds - shipSeconds, rate);
    const key = formatDuration(earthSeconds) + '|' + formatDuration(shipSeconds) + '|' + o.behind + '|' + o.pace;
    if (key === this.lastClockText) return;
    this.lastClockText = key;
    this.ckE.textContent = formatDuration(earthSeconds); this.ckY.textContent = formatDuration(shipSeconds);
    this.ckO.textContent = o.behind; this.ckP.textContent = o.pace;
  }
  setHint(t: string) { if (t === this.lastHint) return; this.lastHint = t; this.hint.textContent = t; }
  setSound(on: boolean) { this.bSound.textContent = on ? 'Sound on' : 'Sound off'; this.bSound.setAttribute('aria-pressed', String(on)); }
  setPaused(p: boolean) { this.bPause.textContent = p ? 'Resume' : 'Pause'; this.bPause.setAttribute('aria-pressed', String(p)); }
  setQuality(cinematic: boolean) {
    this.bQuality.textContent = cinematic ? 'Cinematic' : 'Fast'; this.bQuality.setAttribute('aria-pressed', String(cinematic));
    this.qualityNote.textContent = cinematic ? QUALITY_NOTE.cinematic : QUALITY_NOTE.fast;
  }
  setPlayerVisible(v: boolean) { this.player.hidden = !v; }
  setTrack(title: string, playing: boolean) { this.pTitle.textContent = title; this.pPlay.innerHTML = playing ? '&#10074;&#10074;' : '&#9654;'; }

  /** Names pinned to things on screen. Reuses elements; cheap enough to call every frame. */
  setLabels(list: ScreenLabel[]) {
    while (this.labelEls.length < list.length) { const el = document.createElement('span'); el.className = 'label'; this.labelsRoot.appendChild(el); this.labelEls.push(el); }
    for (let i = 0; i < this.labelEls.length; i++) {
      const el = this.labelEls[i], l = list[i];
      if (!l) { el.hidden = true; continue; }
      el.hidden = false;
      if (el.textContent !== l.text) el.textContent = l.text;
      el.style.transform = `translate(${Math.round(l.x)}px, ${Math.round(l.y)}px)`;
    }
  }
}
